class KrasisHomeEvidence extends HTMLElement {
  constructor() {
    super();
    this.scrollFrame = null;
    this.scrollAnimationFrame = null;
    this.scrollEndTimer = null;
    this.panelEntryTimer = null;
    this.isProgrammaticScroll = false;
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    if (this.dataset.evidenceReady === 'true') return;

    this.viewport = this.querySelector('[data-evidence-viewport]');
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[data-evidence-tab]')];
    this.slides = [...this.querySelectorAll('[data-evidence-slide]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.desktopLayout = window.matchMedia('(min-width: 750px)');

    if (!this.viewport || this.tabs.length === 0 || this.slides.length === 0) return;

    this.dataset.evidenceReady = 'true';

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.setActive(index, { scroll: true }));
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index));
    });

    this.slides.forEach((slide, index) => {
      slide.addEventListener('click', (event) => {
        if (!this.hoverCapable.matches || slide.classList.contains('is-active')) return;

        event.preventDefault();
        this.setActive(index, { scroll: true });
      });
    });

    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    this.setActive(0, { scroll: true, behavior: 'auto' });
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    if (this.panelEntryTimer) clearTimeout(this.panelEntryTimer);
    this.viewport?.style.removeProperty('scroll-snap-type');
  }

  animateDesktopScroll(left) {
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
    }

    const startLeft = this.viewport.scrollLeft;
    const distance = left - startLeft;
    const duration = 800;
    const startTime = performance.now();

    if (Math.abs(distance) < 1) {
      this.viewport.scrollLeft = left;
      this.viewport.style.removeProperty('scroll-snap-type');
      return;
    }

    this.viewport.style.setProperty('scroll-snap-type', 'none');
    this.isProgrammaticScroll = true;

    const step = (timestamp) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress =
        progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      this.viewport.scrollLeft = startLeft + distance * easedProgress;

      if (progress < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(step);
        return;
      }

      this.scrollAnimationFrame = null;
      this.viewport.scrollLeft = left;
      this.viewport.style.removeProperty('scroll-snap-type');

      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
      this.isProgrammaticScroll = false;
      this.handleScroll();
    };

    this.scrollAnimationFrame = requestAnimationFrame(step);
  }

  handleTabKeydown(event, currentIndex) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = Math.min(currentIndex + 1, this.tabs.length - 1);
    }

    if (event.key === 'ArrowLeft') {
      nextIndex = Math.max(currentIndex - 1, 0);
    }
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    this.tabs[nextIndex].focus();
    this.setActive(nextIndex, { scroll: true });
  }

  handleScroll() {
    if (this.isProgrammaticScroll) {
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(() => {
        this.scrollEndTimer = null;
        this.isProgrammaticScroll = false;
        this.handleScroll();
      }, 140);
      return;
    }

    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const viewportCenter = this.viewport.getBoundingClientRect().left + this.viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const bounds = slide.getBoundingClientRect();
        const slideCenter = bounds.left + bounds.width / 2;
        const distance = Math.abs(slideCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.setActive(closestIndex, { scroll: false });
    });
  }

  setActive(index, { scroll = false, behavior } = {}) {
    const nextSlide = this.slides[index];
    if (!nextSlide) return;

    const currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    const currentSlide = this.slides[currentIndex];
    const shouldAnimatePanel =
      currentIndex !== index &&
      this.desktopLayout.matches &&
      this.hoverCapable.matches &&
      !this.reducedMotion.matches;

    if (this.panelEntryTimer) {
      clearTimeout(this.panelEntryTimer);
      this.panelEntryTimer = null;
    }

    this.slides.forEach((slide) => slide.classList.remove('is-entering', 'is-exiting'));

    this.tablist?.setAttribute('data-active-index', String(index));

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      const panel = slide.querySelector('.krasis-home-evidence__panel');
      slide.classList.toggle('is-active', isActive);

      if (isActive) {
        slide.removeAttribute('aria-hidden');
        panel?.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        panel?.setAttribute('inert', '');
      }
    });

    if (shouldAnimatePanel) {
      currentSlide?.classList.add('is-exiting');
      nextSlide.classList.add('is-entering');
      this.panelEntryTimer = setTimeout(() => {
        currentSlide?.classList.remove('is-exiting');
        nextSlide.classList.remove('is-entering');
        this.panelEntryTimer = null;
      }, 1100);
    }

    if (!scroll) return;

    const scrollBehavior = behavior || (this.reducedMotion.matches ? 'auto' : 'smooth');
    const left = nextSlide.offsetLeft - (this.viewport.clientWidth - nextSlide.offsetWidth) / 2;

    if (scrollBehavior === 'smooth' && this.hoverCapable.matches) {
      this.animateDesktopScroll(left);
      return;
    }

    this.isProgrammaticScroll = scrollBehavior === 'smooth';

    this.viewport.scrollTo({
      left,
      behavior: scrollBehavior,
    });
  }
}

if (!customElements.get('krasis-home-evidence')) {
  customElements.define('krasis-home-evidence', KrasisHomeEvidence);
}
